import { CampaignTypeFilter } from 'modules/domains/campaign';
import { match } from 'ts-pattern';
import { pluralizeWord } from '../lib/utils/miscUtils';
import { MetricType } from './components/constants';
import {
  ComparatorDisplayText,
  ComparisonOperator,
  METRICS_TO_DISPLAY_NAMES,
  MetricName,
  Preposition,
  ThresholdType,
} from './constants';
import { DestinationsFormValues } from './context';
import {
  AlertMetricsByMedium,
  AllMessageMediums,
  ParsedKey,
  THours,
  TimeDuration,
  WebhookAuthType,
  WebhookFormField,
} from './types';

/**
 * Translates duration time value (as returned by backend) into the form used in UI
 * components and React Hook Form
 *
 * @param duration
 * @returns frequency integer as string
 *
 * @example
 * parseHourDurationToFrequency('PT24H') // returns 24
 */
export const parseHourDurationToFrequency = (duration?: TimeDuration): THours =>
  duration?.replace(/[^0-9]/g, '') as THours;

/**
 * Reverse of function above, used to format values for request payloads
 * to save alerts to backend
 *
 * @param frequency
 * @returns duration formatted as https://en.wikipedia.org/wiki/ISO_8601#Durations
 *
 * @example
 * parseHourFrequencyToDuration('24') // returns PT24H
 */
export const parseHourFrequencyToDuration = (frequency: THours): TimeDuration => `PT${frequency}H`;

export const getTypeKey = (valueType?: MetricType): ThresholdType =>
  valueType === MetricType.Number ? ThresholdType.Number : ThresholdType.Percentage;

export const parseAlertKey = (key: string) => key.split('-') as ParsedKey;

export const getMetricData = ({
  messageMedium,
  metricName,
  allAlertMetrics,
}: {
  messageMedium: AllMessageMediums;
  metricName: MetricName;
  allAlertMetrics: AlertMetricsByMedium;
}) => allAlertMetrics?.[messageMedium]?.find((m) => m.metricName === metricName);

export const getWebhookUpsertVariables = ({
  formValues,
  dirtyFields,
  isEdit = false,
}: {
  formValues: DestinationsFormValues;
  dirtyFields?: Record<string, boolean>;
  isEdit?: boolean;
}) => {
  const { name, authType, endpoint } = formValues;
  const commonFields = { enabled: true, name, endpoint, authType };

  const dirtyFieldKeys = isEdit ? Object.keys(dirtyFields || {}) : [];
  const isAuthTokenDirty = dirtyFieldKeys.includes(WebhookFormField.AuthToken);
  const isClientSecretDirty = dirtyFieldKeys.includes(WebhookFormField.ClientSecret);

  switch (authType) {
    case WebhookAuthType.BASIC:
      return {
        ...commonFields,
        ...(isEdit && !isAuthTokenDirty ? {} : { authToken: formValues.authToken }), // omit field if unchanged when editing
      };

    case WebhookAuthType.OAUTH2:
      return {
        ...commonFields,
        clientId: formValues.clientId,
        ...(isEdit && !isClientSecretDirty ? {} : { clientSecret: formValues.clientSecret }), // omit field if unchanged when editing
        tokenEndpoint: formValues.tokenEndpoint,
      };

    default:
      // WebhookAuthType.NONE
      return commonFields;
  }
};

/**
 * Alert display text utils
 */

export const getMetricName = (metricName: MetricName) => METRICS_TO_DISPLAY_NAMES[metricName];

export const getPreposition = (isScheduled: boolean, isRecurring = true) =>
  match({ isScheduled, isRecurring })
    .with({ isScheduled: true }, () => Preposition.After)
    .with({ isScheduled: false, isRecurring: true }, () => Preposition.WithinEvery)
    .otherwise(() => Preposition.Within);

export const getTimeUnits = (frequency: THours) => pluralizeWord('hour', frequency);

export const getSuffix = (campaignType: CampaignTypeFilter) => {
  const isScheduled = campaignType === CampaignTypeFilter.Blast;
  const isEligibility = campaignType === CampaignTypeFilter.Eligibility;

  return match({ isScheduled, isEligibility })
    .with({ isScheduled: true }, () => ' of sending')
    .with({ isEligibility: true }, () => ' of starting')
    .otherwise(() => '');
};

export const getComparator = (comparator: ComparisonOperator) => ComparatorDisplayText[comparator];
